import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Tickets statistical hooks — phase-05 dashboard tab.
 * All endpoints are `GET /tickets/statistical_*?start_date=&end_date=`.
 * Types ported verbatim from legacy `types/response.ts`.
 */

/* ── Shared params ───────────────────────────────────────────────────────── */

export interface StatDateParams {
  start_date: string;
  end_date: string;
}

export interface StatPeriodParams extends StatDateParams {
  period?: 'day' | 'week' | 'month';
}

/* ── Statistical types (verbatim from legacy types/response.ts) ──────────── */

export type TicketStatusType = 'opening' | 'work-in-progress' | 'escalated' | 'closed' | 'completed';

export interface TicketStatusSummary {
  ticket_status: TicketStatusType;
  total_tickets: number;
}

export type TicketPriorityStat = 'low' | 'medium' | 'high';

export interface TicketAvgPriority {
  avg_resolution_time: number;
  ticket_priority: TicketPriorityStat;
  total_tickets: number;
  resolved_tickets: number;
  pending_tickets: number;
}

export interface TicketHandler {
  employee_id: number;
  employee_name: string;
  total_tickets: number;
  resolved_tickets: number;
}

export interface OverdueTicketsByEmployee {
  employee_id: number;
  employee_name: string;
  total_tickets: number;
  ontime_tickets: number;
  overdue_tickets: number;
  overdue_tickets_closed: number;
  overdue_tickets_closed_on_time: number;
}

export interface TicketStatusPeriod {
  period: string;
  open_tickets: number;
  closed_tickets: number;
}

export interface TicketTypeDetail {
  ticket_type_id: number;
  ticket_type_title: string;
  ticket_type_key: string;
  total_tickets: number;
  percentage: number;
}

export interface TicketsByType {
  ticket_type_group: string;
  group_total: number;
  group_percentage: number;
  ticket_types: TicketTypeDetail[];
}

export interface TicketsBySource {
  ticket_source: string;
  total_tickets: number;
  percentage: number;
}

export interface TicketsByProduct {
  product_group_name: string;
  total_tickets: number;
  percentage: number;
}

export interface TopCustomerByTickets {
  customer_id: number;
  customer_name: string;
  company_name: string;
  total_tickets: number;
}

export interface MeetingTicketsByPurpose {
  ticket_meeting_purpose: string;
  total_tickets: number;
  percentage: number;
}

export interface AssigneeWorkload {
  employee_id: number;
  employee_name: string;
  employee_email: string;
  open_tickets: number;
  high_priority: number;
  medium_priority: number;
  low_priority: number;
  overdue: number;
}

export interface CustomerSatisfaction {
  total_rated: number;
  avg_rating: number;
  five_star: number;
  four_star: number;
  three_star: number;
  two_star: number;
  one_star: number;
}

export interface SLACompliance {
  total_tickets: number;
  ontime_count: number;
  late_count: number;
  pending_count: number;
  ontime_percentage: number;
}

export interface AvgFirstResponseTime {
  avg_first_response_hours: number;
  by_priority: { priority: string; avg_hours: number }[];
}

export interface TicketResolutionRate {
  total_tickets: number;
  resolved_tickets: number;
  resolution_rate: number;
  by_type: { ticket_type_title: string; total: number; resolved: number; rate: number }[];
}

export interface MajorIncidentsSummary {
  total_major_incidents: number;
  resolved: number;
  in_progress: number;
  avg_resolution_time_hours: number;
  by_product: { product_group_name: string; count: number }[];
}

export interface TicketCreationTrend {
  period: string;
  total_created: number;
  by_type: { ticket_type_title: string; count: number }[];
}

export type TicketTypeGroup =
  | 'support_request'
  | 'error_service'
  | 'service_request'
  | 'service'
  | 'meeting'
  | 'change'
  | 'internal';

export interface TicketCountByTypeGroup {
  ticket_type_group: TicketTypeGroup;
  total_tickets: number;
}

export interface TicketsByEmployeeAndType {
  employee_id: number;
  employee_name: string;
  ticket_counts: TicketCountByTypeGroup[];
  total_tickets: number;
  escalated_tickets?: number;
}

export interface EscalatedTicketItem {
  ticket_id: number;
  ticket_content: string;
  ticket_type_title: string;
  ticket_status: string;
  ticket_requested_at: string;
  escalated_at: string | null;
  escalated_by_id: number | null;
  escalated_by_name: string;
  reason: string;
}

/* ── Query key factory ───────────────────────────────────────────────────── */

const statKeys = {
  statusSummary: (p: StatDateParams) => ['tickets', 'stat', 'status-summary', p] as const,
  avgResolutionByPriority: (p: StatDateParams) => ['tickets', 'stat', 'avg-resolution-priority', p] as const,
  topHandlers: (p: StatDateParams) => ['tickets', 'stat', 'top-handlers', p] as const,
  overdueByEmployee: (p: StatDateParams) => ['tickets', 'stat', 'overdue-by-employee', p] as const,
  statusCountByPeriod: (p: StatPeriodParams) => ['tickets', 'stat', 'status-count-period', p] as const,
  byType: (p: StatDateParams) => ['tickets', 'stat', 'by-type', p] as const,
  bySource: (p: StatDateParams) => ['tickets', 'stat', 'by-source', p] as const,
  byProduct: (p: StatDateParams) => ['tickets', 'stat', 'by-product', p] as const,
  topCustomers: (p: StatDateParams) => ['tickets', 'stat', 'top-customers', p] as const,
  meetingByPurpose: (p: StatDateParams) => ['tickets', 'stat', 'meeting-by-purpose', p] as const,
  assigneeWorkload: (p: StatDateParams) => ['tickets', 'stat', 'assignee-workload', p] as const,
  customerSatisfaction: (p: StatDateParams) => ['tickets', 'stat', 'customer-satisfaction', p] as const,
  slaCompliance: (p: StatDateParams) => ['tickets', 'stat', 'sla-compliance', p] as const,
  avgFirstResponse: (p: StatDateParams) => ['tickets', 'stat', 'avg-first-response', p] as const,
  resolutionRate: (p: StatDateParams) => ['tickets', 'stat', 'resolution-rate', p] as const,
  majorIncidents: (p: StatDateParams) => ['tickets', 'stat', 'major-incidents', p] as const,
  creationTrend: (p: StatPeriodParams) => ['tickets', 'stat', 'creation-trend', p] as const,
  byEmployeeAndType: (p: StatDateParams) => ['tickets', 'stat', 'by-employee-type', p] as const,
  escalatedByEmployee: (p: { employee_id: number; start_date: string; end_date: string; page: number; page_size: number }) =>
    ['tickets', 'stat', 'escalated-by-employee', p] as const,
};

/* ── Helper ──────────────────────────────────────────────────────────────── */

function toQuery(params: object): string {
  return new URLSearchParams(
    Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => [k, String(v)]),
    ),
  ).toString();
}

/* ── Hooks ───────────────────────────────────────────────────────────────── */

export function useTicketStatusSummary(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.statusSummary(params),
    queryFn: async (): Promise<ResponseArray<TicketStatusSummary>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_ticket_status_summary?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useAvgResolutionTimeByPriority(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.avgResolutionByPriority(params),
    queryFn: async (): Promise<ResponseArray<TicketAvgPriority>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_avg_resolution_time_by_priority?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTopTicketHandlers(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.topHandlers(params),
    queryFn: async (): Promise<ResponseArray<TicketHandler>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_top_ticket_handlers?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useOverdueTicketsByEmployee(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.overdueByEmployee(params),
    queryFn: async (): Promise<ResponseArray<OverdueTicketsByEmployee>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_overdue_tickets_by_employee?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTicketStatusCountByPeriod(params: StatPeriodParams) {
  return useQuery({
    queryKey: statKeys.statusCountByPeriod(params),
    queryFn: async (): Promise<ResponseArray<TicketStatusPeriod>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_ticket_status_count_by_period?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTicketsByType(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.byType(params),
    queryFn: async (): Promise<ResponseArray<TicketsByType>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_tickets_by_type?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTicketsBySource(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.bySource(params),
    queryFn: async (): Promise<ResponseArray<TicketsBySource>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_tickets_by_source?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTicketsByProduct(params: StatDateParams & { limit?: number }) {
  return useQuery({
    queryKey: statKeys.byProduct(params),
    queryFn: async (): Promise<ResponseArray<TicketsByProduct>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_tickets_by_product?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTopCustomersByTickets(params: StatDateParams & { limit?: number }) {
  return useQuery({
    queryKey: statKeys.topCustomers(params),
    queryFn: async (): Promise<ResponseArray<TopCustomerByTickets>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_top_customers_by_tickets?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useMeetingTicketsByPurpose(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.meetingByPurpose(params),
    queryFn: async (): Promise<ResponseArray<MeetingTicketsByPurpose>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_meeting_tickets_by_purpose?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useAssigneeWorkload(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.assigneeWorkload(params),
    queryFn: async (): Promise<ResponseArray<AssigneeWorkload>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_assignee_workload?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useCustomerSatisfaction(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.customerSatisfaction(params),
    queryFn: async (): Promise<ResponseObject<CustomerSatisfaction>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_customer_satisfaction?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useSLACompliance(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.slaCompliance(params),
    queryFn: async (): Promise<ResponseObject<SLACompliance>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_sla_compliance?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useAvgFirstResponseTime(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.avgFirstResponse(params),
    queryFn: async (): Promise<ResponseObject<AvgFirstResponseTime>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_avg_first_response_time?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTicketResolutionRate(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.resolutionRate(params),
    queryFn: async (): Promise<ResponseObject<TicketResolutionRate>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_ticket_resolution_rate?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useMajorIncidentsSummary(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.majorIncidents(params),
    queryFn: async (): Promise<ResponseObject<MajorIncidentsSummary>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_major_incidents_summary?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTicketCreationTrend(params: StatPeriodParams) {
  return useQuery({
    queryKey: statKeys.creationTrend(params),
    queryFn: async (): Promise<ResponseArray<TicketCreationTrend>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_ticket_creation_trend?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useTicketsByEmployeeAndType(params: StatDateParams) {
  return useQuery({
    queryKey: statKeys.byEmployeeAndType(params),
    queryFn: async (): Promise<ResponseArray<TicketsByEmployeeAndType>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_ticket_by_employee_and_ticket_type_group?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}

export function useEscalatedTicketsByEmployee(params: {
  employee_id: number;
  start_date: string;
  end_date: string;
  page: number;
  page_size: number;
}) {
  return useQuery({
    queryKey: statKeys.escalatedByEmployee(params),
    enabled: !!params.employee_id,
    queryFn: async (): Promise<ResponseArray<EscalatedTicketItem>> => {
      const res = await dataCentralApi.get(
        `/tickets/statistical_ticket_by_employee_escalated?${toQuery(params)}`,
      );
      return res.data;
    },
  });
}
